import {
  type GameId,
  type GameMetadata,
  type GamePricingVariant,
} from "modules/common/model/models";
import {
  GENERIC_DECK_ADAPTER,
  MTG_DECK_ADAPTER,
  ONE_PIECE_DECK_ADAPTER,
  RIFTBOUND_DECK_ADAPTER,
  type DeckGameAdapter,
} from "modules/decks/gameAdapters";
import {
  FLESH_AND_BLOOD_CARD_PRESENTATION_ADAPTER,
  FUSION_WORLD_CARD_PRESENTATION_ADAPTER,
  GENERIC_CARD_PRESENTATION_ADAPTER,
  LORCANA_CARD_PRESENTATION_ADAPTER,
  MTG_CARD_PRESENTATION_ADAPTER,
  ONE_PIECE_CARD_PRESENTATION_ADAPTER,
  POKEMON_CARD_PRESENTATION_ADAPTER,
  RIFTBOUND_CARD_PRESENTATION_ADAPTER,
  STAR_WARS_UNLIMITED_CARD_PRESENTATION_ADAPTER,
  type CardPresentationAdapter,
} from "modules/cards/presentation/gameAdapters";
import {
  renderCodeSetIcon,
  renderFleshAndBloodBrandMark,
  renderFleshAndBloodGameIcon,
  renderFusionWorldBrandMark,
  renderFusionWorldGameIcon,
  renderGenericGameIcon,
  renderKeyruneSetIcon,
  renderKeyruneSetIconChip,
  renderLorcanaGameIcon,
  renderOnePieceGameIcon,
  renderOnePieceSetIcon,
  renderPlaneswalkerGameIcon,
  renderPokemonGameIcon,
  renderPokemonSetIcon,
  renderPokemonSetIconChip,
  renderRiftboundGameIcon,
  renderStarWarsUnlimitedBrandMark,
  renderStarWarsUnlimitedGameIcon,
  type GameIconRenderer,
  type SetIconRenderer,
} from "modules/common/presentation/gameRenderers";
import {
  renderCodePreviewSetIcon,
  renderKeyrunePreviewSetIcon,
  renderPokemonPreviewSetIcon,
  type PreviewSetIconRenderer,
} from "modules/common/presentation/previewRenderers";

export interface PreviewLogoAssets {
  planeswalker: string;
  riftbound: string;
  pokemon: string;
}

export interface GamePresentationProfile {
  /** Compact, square mark for selectors, facets, and other small controls. */
  renderIcon: GameIconRenderer;
  /** Optional full logo for large decorative brand surfaces. */
  renderBrandMark?: GameIconRenderer;
  renderSetIcon: SetIconRenderer;
  /** Optional self-contained mark for dense tables that need a fixed icon target. */
  renderSetIconChip?: SetIconRenderer;
  renderPreviewSetIcon: PreviewSetIconRenderer;
  catalogueWatermark?: {
    opacity: number;
    size: number;
  };
  setTable: {
    showIcon: boolean;
    showTotalSize: boolean;
    showType: boolean;
  };
  cardTable: {
    costExcludedTypes?: readonly string[];
    showFormatFilter: boolean;
  };
  facetIcons: {
    cardType: boolean;
  };
  showSetFilter: boolean;
  deckMainExcludedTypes?: readonly string[];
  facetAccentClass: string;
  profileBannerClass?: string;
  resolvePreviewLogo(assets: PreviewLogoAssets): string;
  seo?: {
    setTitle?: string;
    setDescription?: string;
    deckTitle?: string;
    deckDescription?: string;
    scannerTitle?: string;
    scannerDescription?: string;
  };
}

export interface SetNavigationPolicy {
  /** Card-to-set links may use parent metadata embedded in the card response. */
  linkToEmbeddedSet?: boolean;
  /** Related-set route aliases may redirect to the API-provided canonical code. */
  canonicalizeAliases?: boolean;
}

export interface GameRegistration extends GameMetadata {
  /** Legacy-only entry excluded from the released-game integration contract. */
  compatibilityOnly?: boolean;
  /** Required recognition-model registration. */
  scannerModel: string;
  /** Specialized deck behavior bundled for this game. */
  deck?: DeckGameAdapter;
  /** Specialized card data access bundled for this game. */
  card: CardPresentationAdapter;
  /** Small, declarative exceptions for set routing and catalogue links. */
  setNavigation?: SetNavigationPolicy;
  /** Declarative UI behavior consumed by every web surface. */
  presentation: GamePresentationProfile;
  /** Pricing routes bundled for this game, keyed by the server variant. */
  pricingMode: "feeds" | "unavailable";
  pricing: Partial<
    Record<GamePricingVariant, { marketplaceProductPath?: string }>
  >;
}

type GameRegistrationInput = Omit<GameRegistration, "id" | "scannerModel"> & {
  /** Defaults to the canonical game ID; set only when the model uses another ID. */
  scannerModel?: string;
};
type DefinedGameRegistry<T extends Record<string, GameRegistrationInput>> = {
  readonly [K in keyof T]: T[K] & {
    readonly id: K;
    readonly scannerModel: string;
  };
};

/**
 * The single client-side registration point for bundled games. Runtime product
 * exposure still comes from the server capability manifest; this registry
 * links code-owned metadata and optional specialized implementations.
 */
function defineGameRegistry<
  const T extends Record<string, GameRegistrationInput>,
>(registrations: T): DefinedGameRegistry<T> {
  for (const [gameId, registration] of Object.entries(registrations)) {
    if (!(registration.scannerModel ?? gameId).trim()) {
      throw new Error(`Game ${gameId} is missing its mandatory ML model`);
    }
    const pricingRoutes = Object.keys(registration.pricing).length;
    if (
      (registration.pricingMode === "feeds" && pricingRoutes === 0) ||
      (registration.pricingMode === "unavailable" && pricingRoutes !== 0)
    ) {
      throw new Error(`Game ${gameId} has an invalid pricing declaration`);
    }
    if (
      !registration.compatibilityOnly &&
      registration.pricingMode === "feeds" &&
      (!registration.pricing["cardmarket-eur"] ||
        !registration.pricing["tcgplayer-usd"])
    ) {
      throw new Error(
        `Game ${gameId} must register Cardmarket EUR and TCGplayer USD pricing`
      );
    }
    if (
      !registration.compatibilityOnly &&
      registration.pricingMode === "feeds" &&
      !registration.pricing["cardmarket-eur"]?.marketplaceProductPath?.trim()
    ) {
      throw new Error(`Game ${gameId} is missing its Cardmarket product path`);
    }
  }
  return Object.fromEntries(
    Object.entries(registrations).map(([gameId, registration]) => [
      gameId,
      {
        ...registration,
        id: gameId,
        scannerModel: registration.scannerModel ?? gameId,
      },
    ])
  ) as DefinedGameRegistry<T>;
}

export const GAME_REGISTRY = defineGameRegistry({
  mtg: {
    name: "Magic: The Gathering",
    shortName: "MTG",
    description: "Explore the world's most popular trading card game",
    brandColor: "#0E68AB",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "Magic" },
      "tcgplayer-usd": {},
    },
    deck: MTG_DECK_ADAPTER,
    card: MTG_CARD_PRESENTATION_ADAPTER,
    setNavigation: {
      linkToEmbeddedSet: true,
      canonicalizeAliases: true,
    },
    presentation: {
      renderIcon: renderPlaneswalkerGameIcon,
      renderSetIcon: renderKeyruneSetIcon,
      renderSetIconChip: renderKeyruneSetIconChip,
      renderPreviewSetIcon: renderKeyrunePreviewSetIcon,
      setTable: { showIcon: true, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: true,
      },
      facetIcons: { cardType: true },
      showSetFilter: true,
      facetAccentClass: "border-primary/60",
      profileBannerClass:
        "bg-gradient-to-r from-amber-500/25 via-orange-400/10 to-transparent",
      resolvePreviewLogo: (assets) => assets.planeswalker,
      seo: {
        setTitle: "Magic: The Gathering Card Sets - Browse All MTG Expansions",
        setDescription:
          "Browse all Magic: The Gathering card sets and expansions. Search MTG cards by set, compare prices, and find local trades on Lootstack.",
        deckTitle: "MTG Community Decks - Commander, Modern & Standard Builds",
        deckDescription:
          "Browse Magic: The Gathering community decks. Find Commander, Modern, Standard, and Pioneer deck builds, strategies, and card lists on Lootstack.",
        scannerTitle: "MTG Card Scanner App - Scan Magic Cards",
        scannerDescription:
          "Scan Magic: The Gathering cards into a collection or local trade list. Exact printing, language, and foil finish, then TCGPlayer or Cardmarket prices.",
      },
    },
  },
  riftbound: {
    name: "Riftbound",
    shortName: "Riftbound",
    description: "Discover innovative gameplay and card mechanics",
    brandColor: "#EF7D00",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "Riftbound" },
      "tcgplayer-usd": {},
    },
    deck: RIFTBOUND_DECK_ADAPTER,
    card: RIFTBOUND_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderRiftboundGameIcon,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      setTable: { showIcon: false, showTotalSize: false, showType: false },
      cardTable: {
        costExcludedTypes: ["Legend", "Battlefield", "Rune"],
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: false,
      deckMainExcludedTypes: ["Legend", "Battlefield", "Rune"],
      facetAccentClass: "border-secondary/60",
      profileBannerClass:
        "bg-gradient-to-r from-indigo-500/25 via-violet-400/10 to-transparent",
      resolvePreviewLogo: (assets) => assets.riftbound,
      seo: {
        setTitle: "Riftbound Card Sets & Expansions - Browse All Sets",
        deckTitle: "Riftbound Community Decks & Strategies",
        deckDescription:
          "Browse Riftbound community decks. Discover deck builds, strategies, and share your Riftbound card game decks on Lootstack.",
        scannerTitle: "Riftbound Card Scanner - Scan & Price Cards",
        scannerDescription:
          "Scan Riftbound cards into a collection or local trade list. Exact printing, language, and foil finish, then TCGPlayer or Cardmarket prices.",
      },
    },
  },
  pokemon: {
    compatibilityOnly: true,
    name: "Pokémon",
    shortName: "Pokémon",
    description: "Explore the Pokémon Trading Card Game",
    brandColor: "#FFCB05",
    hidden: true,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "Pokemon" },
    },
    card: POKEMON_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderPokemonGameIcon,
      renderSetIcon: renderPokemonSetIcon,
      renderSetIconChip: renderPokemonSetIconChip,
      renderPreviewSetIcon: renderPokemonPreviewSetIcon,
      setTable: { showIcon: true, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: false,
      facetAccentClass: "border-accent/60",
      profileBannerClass:
        "bg-gradient-to-r from-red-500/25 via-yellow-400/10 to-transparent",
      resolvePreviewLogo: (assets) => assets.pokemon,
      seo: {
        setTitle: "Pokemon Card Sets & Expansions - Browse All Sets",
        deckTitle: "Pokemon Community Decks & Strategies",
        deckDescription:
          "Browse Pokemon community decks. Discover deck builds, strategies, and share your Pokemon card game decks on Lootstack.",
      },
    },
  },
  onepiece: {
    name: "One Piece Card Game",
    shortName: "One Piece",
    description: "Set sail with the Straw Hat Pirates and beyond",
    brandColor: "#E60012",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "OnePiece" },
      "tcgplayer-usd": {},
    },
    deck: ONE_PIECE_DECK_ADAPTER,
    card: ONE_PIECE_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderOnePieceGameIcon,
      renderSetIcon: renderOnePieceSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      setTable: { showIcon: true, showTotalSize: true, showType: false },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: true },
      showSetFilter: false,
      facetAccentClass: "border-base-content/20",
      profileBannerClass:
        "bg-gradient-to-r from-sky-500/25 via-blue-400/10 to-transparent",
      resolvePreviewLogo: () => "",
      seo: {
        setTitle:
          "One Piece Card Game Sets & Boosters - Browse All OPTCG Releases",
        deckTitle: "One Piece Card Game Decks & Strategies",
        deckDescription:
          "Browse One Piece Card Game decks. Discover Leader builds, deck strategies, and share your OPTCG decks on Lootstack.",
        scannerTitle: "One Piece Card Scanner (OPTCG) - Scan & Identify Cards",
        scannerDescription:
          "Scan One Piece Card Game (OPTCG) cards into a collection or local trade list. Exact printing, language, and foil finish, then TCGPlayer or Cardmarket prices.",
      },
    },
  },
  starwarsunlimited: {
    name: "Star Wars: Unlimited",
    shortName: "SWU",
    description: "Collect and trade cards from across the Star Wars galaxy",
    brandColor: "#F5B335",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "StarWarsUnlimited" },
      "tcgplayer-usd": {},
    },
    card: STAR_WARS_UNLIMITED_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderStarWarsUnlimitedGameIcon,
      renderBrandMark: renderStarWarsUnlimitedBrandMark,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      setTable: { showIcon: false, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: true,
      facetAccentClass: "border-base-content/20",
      profileBannerClass:
        "bg-gradient-to-r from-warning/25 via-error/10 to-transparent",
      resolvePreviewLogo: () => "",
      seo: {
        setTitle: "Star Wars Unlimited Card Sets & Expansions",
        setDescription:
          "Browse Star Wars Unlimited cards by set, compare EUR and USD prices, and find local trades on Lootstack.",
      },
    },
  },
  lorcana: {
    name: "Disney Lorcana",
    shortName: "Lorcana",
    description: "Trade Disney characters across the Inklands",
    brandColor: "#6D3FA0",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "Lorcana" },
      "tcgplayer-usd": {},
    },
    card: LORCANA_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderLorcanaGameIcon,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      setTable: { showIcon: false, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: true,
      facetAccentClass: "border-base-content/20",
      profileBannerClass:
        "bg-gradient-to-r from-secondary/25 via-accent/10 to-transparent",
      resolvePreviewLogo: () => "",
      seo: {
        setTitle: "Disney Lorcana Card Sets & Expansions",
        setDescription:
          "Browse Disney Lorcana cards by set, compare EUR and USD prices, and find local trades on Lootstack.",
      },
    },
  },
  dbsfusionworld: {
    name: "Dragon Ball Super: Fusion World",
    shortName: "Fusion World",
    description: "Collect iconic Dragon Ball battles and parallel artwork",
    brandColor: "#F05A24",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "DragonBallSuper" },
      "tcgplayer-usd": {},
    },
    card: FUSION_WORLD_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderFusionWorldGameIcon,
      renderBrandMark: renderFusionWorldBrandMark,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      catalogueWatermark: { opacity: 0.16, size: 124 },
      setTable: { showIcon: true, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: true,
      facetAccentClass: "border-base-content/20",
      profileBannerClass:
        "bg-gradient-to-r from-warning/25 via-error/10 to-transparent",
      resolvePreviewLogo: () => "",
      seo: {
        setTitle: "Dragon Ball Super Fusion World Card Sets",
        setDescription:
          "Browse Dragon Ball Super Fusion World cards, compare EUR and USD prices, and find local trades on Lootstack.",
      },
    },
  },
  fleshandblood: {
    name: "Flesh and Blood",
    shortName: "FaB",
    description: "Collect and trade heroes, equipment, and premium foils",
    brandColor: "#A6192E",
    hidden: false,
    pricingMode: "feeds",
    pricing: {
      "cardmarket-eur": { marketplaceProductPath: "FleshAndBlood" },
      "tcgplayer-usd": {},
    },
    card: FLESH_AND_BLOOD_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderFleshAndBloodGameIcon,
      renderBrandMark: renderFleshAndBloodBrandMark,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      catalogueWatermark: { opacity: 0.28, size: 128 },
      setTable: { showIcon: false, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: true,
      facetAccentClass: "border-error/50",
      profileBannerClass:
        "bg-gradient-to-r from-error/25 via-warning/10 to-transparent",
      resolvePreviewLogo: () => "",
      seo: {
        setTitle: "Flesh and Blood Card Sets & Expansions",
        setDescription:
          "Browse Flesh and Blood cards by physical printing, compare EUR and USD prices, and find local trades on Lootstack.",
      },
    },
  },
});

function gameNameFromId(id: GameId): string {
  return id
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function bundledGameMetadata(
  game: GameId
): GameRegistration | undefined {
  return GAME_REGISTRY[game as keyof typeof GAME_REGISTRY];
}

export function isBundledGameId(
  game: GameId
): game is keyof typeof GAME_REGISTRY {
  return bundledGameMetadata(game) !== undefined;
}

export function getGameMetadata(game: GameId): GameRegistration {
  const bundled = bundledGameMetadata(game);
  if (bundled) return bundled;

  const name = gameNameFromId(game);
  return {
    id: game,
    name,
    shortName: name,
    description: `Explore ${name} cards`,
    brandColor: "#64748B",
    hidden: false,
    pricingMode: "unavailable",
    pricing: {},
    scannerModel: game,
    card: GENERIC_CARD_PRESENTATION_ADAPTER,
    presentation: {
      renderIcon: renderGenericGameIcon,
      renderSetIcon: renderCodeSetIcon,
      renderPreviewSetIcon: renderCodePreviewSetIcon,
      setTable: { showIcon: true, showTotalSize: true, showType: true },
      cardTable: {
        showFormatFilter: false,
      },
      facetIcons: { cardType: false },
      showSetFilter: true,
      facetAccentClass: "border-base-content/20",
      resolvePreviewLogo: () => "",
    },
  };
}

export type BundledGameId = keyof typeof GAME_REGISTRY;

export const DEFAULT_GAME_ID: GameId = GAME_REGISTRY.mtg.id;
export const DEFAULT_DECK_GAME_ID: GameId = DEFAULT_GAME_ID;

export function getDeckGameAdapter(game: GameId | undefined): DeckGameAdapter {
  return (game && bundledGameMetadata(game)?.deck) || GENERIC_DECK_ADAPTER;
}

export function getCardPresentationAdapter(
  game: GameId | undefined
): CardPresentationAdapter {
  return (
    (game && bundledGameMetadata(game)?.card) ||
    GENERIC_CARD_PRESENTATION_ADAPTER
  );
}

export function getCatalogueSetCode(
  game: GameId | undefined,
  card: { setCode: string; set?: { code?: string } }
): string {
  const policy = game ? bundledGameMetadata(game)?.setNavigation : undefined;
  return policy?.linkToEmbeddedSet
    ? card.set?.code?.trim() || card.setCode
    : card.setCode;
}

export function getCanonicalSetPageCode(
  game: GameId | undefined,
  set: { canonicalCode?: string },
  requestedCode: string
): string {
  const policy = game ? bundledGameMetadata(game)?.setNavigation : undefined;
  return policy?.canonicalizeAliases
    ? set.canonicalCode?.trim() || requestedCode
    : requestedCode;
}

export function registeredDeckGameIds(): GameId[] {
  return Object.values(GAME_REGISTRY)
    .filter(
      (registration) =>
        "deck" in registration && registration.deck !== undefined
    )
    .map((registration) => registration.id);
}
